/**
 * Every line this org can see: the ones it captured as a site, and the ones
 * matched to its own Medicaid enrollments.
 *
 * Confirm is the site's attestation of a QR check-in, so only the site can do it.
 * A member's dispute is answered by whoever filed the line — upheld, corrected
 * (new hours or date), or voided — always with a note the member can read.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Check, RefreshCw } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { PageState } from '../weavein';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { getActiveCboId } from '@/services/api/organizations';
import { type RecordStatus, type VerifyRecord, todayISO, verifyApi } from './api';
import {
  ErrorNote,
  Field,
  NativeSelect,
  RecordStatusBadge,
  activityLabel,
  errMessage,
  fmtDate,
  fmtHours,
  inputClass,
  missingFieldLabel,
  sourceLabel,
} from './shared';

type Scope = 'all' | 'site' | 'enrollment';
type Action = { kind: 'void' | 'resolve'; record: VerifyRecord } | null;

export function VerifyRecords() {
  const { t } = useAppTranslation();
  const [scope, setScope] = useState<Scope>('all');
  const [status, setStatus] = useState<RecordStatus | ''>('');
  const [records, setRecords] = useState<VerifyRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [action, setAction] = useState<Action>(null);
  // Only the site that filed a line can void it (the API enforces this too).
  const activeCbo = getActiveCboId();

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setRecords((await verifyApi.records({ scope, status: status || undefined })).records);
    } catch (e) {
      setError(errMessage(e, t('verify.records.load_failed', 'Could not load records.')));
    } finally {
      setLoading(false);
    }
  }, [scope, status, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (r: VerifyRecord) =>
    setRecords((prev) => prev.map((x) => (x.id === r.id ? { ...x, ...r, can_confirm: false, can_resolve: false } : x)));

  const confirm = async (rec: VerifyRecord) => {
    setBusyId(rec.id);
    setError(null);
    try {
      replace(await verifyApi.confirmRecord(rec.id));
    } catch (e) {
      setError(errMessage(e, t('verify.records.confirm_failed', 'Could not confirm.')));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label={t('verify.records.scope', 'Show')}>
          <NativeSelect value={scope} onChange={(e) => setScope(e.target.value as Scope)}>
            <option value="all">{t('verify.records.scope_all', 'Everything I can see')}</option>
            <option value="site">{t('verify.records.scope_site', 'Captured at our sites')}</option>
            <option value="enrollment">{t('verify.records.scope_enrollment', 'Matched to our enrollments')}</option>
          </NativeSelect>
        </Field>
        <Field label={t('verify.records.status', 'Status')}>
          <NativeSelect value={status} onChange={(e) => setStatus(e.target.value as RecordStatus | '')}>
            <option value="">{t('verify.records.status_any', 'Any status')}</option>
            <option value="captured">{t('verify.status.captured', 'Awaiting confirmation')}</option>
            <option value="disputed">{t('verify.status.disputed', 'Disputed')}</option>
            <option value="confirmed">{t('verify.status.confirmed', 'Confirmed')}</option>
            <option value="self_attested">{t('verify.status.self_attested', 'Self-reported')}</option>
            <option value="voided">{t('verify.status.voided', 'Voided')}</option>
          </NativeSelect>
        </Field>
        <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
          <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          {t('verify.refresh', 'Refresh')}
        </Button>
      </div>

      <ErrorNote message={error} />

      {loading && records.length === 0 ? (
        <PageState variant="loading" />
      ) : records.length === 0 ? (
        <PageState
          variant="empty"
          title={t('verify.records.empty_title', 'No lines here')}
          message={t('verify.records.empty', 'Lines appear here once hours are captured or matched.')}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-v2border text-left text-xs uppercase tracking-wide text-ink-500">
                  <tr>
                    <th className="px-3 py-2 font-medium">{t('verify.date', 'Date')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.who', 'Who')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.service', 'Type of service')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('verify.hours', 'Hours')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.where', 'Where · contact')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.filed', 'Filed')}</th>
                    <th className="px-3 py-2 font-medium">{t('verify.records.status', 'Status')}</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {records.map((r) => (
                    <tr key={r.id} className="border-b border-v2border align-top last:border-0">
                      <td className="whitespace-nowrap px-3 py-2 text-ink-700">{fmtDate(r.activity_date)}</td>
                      <td className="px-3 py-2">
                        <div className="text-ink">
                          {r.participant_first_name} {r.participant_last_name}
                        </div>
                        <div className="text-xs text-ink-500">
                          {r.enrollment_id
                            ? t('verify.records.matched', 'Matched to enrollment')
                            : t('verify.records.unmatched', 'Not matched yet')}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-ink">{r.service_description}</div>
                        <div className="text-xs text-ink-500">{activityLabel(t, r.activity_type)}</div>
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-ink">{fmtHours(r.hours)}</td>
                      <td className="px-3 py-2 text-ink-700">
                        <div>{r.site_name || t('verify.records.no_site', 'No organization')}</div>
                        <div className="text-xs text-ink-500">{r.point_of_contact_name || '—'}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="text-ink-700">{sourceLabel(t, r.source)}</div>
                        <div className="text-xs text-ink-500">{r.filed_by_name || r.filed_by_label}</div>
                        {r.confirmed_by_name && (
                          <div className="text-xs text-ink-500">
                            {t('verify.records.confirmed_by', 'Confirmed by {{name}}', { name: r.confirmed_by_name })}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex flex-col items-start gap-1">
                          <RecordStatusBadge status={r.status} t={t} />
                          {r.missing_fields.map((m) => (
                            <Badge key={m} variant="warning" size="sm">
                              {missingFieldLabel(t, m)}
                            </Badge>
                          ))}
                          {r.delivered_at && (
                            <span className="text-xs text-ink-500">
                              {t('verify.records.delivered', 'Delivered {{date}}', { date: fmtDate(r.delivered_at) })}
                            </span>
                          )}
                          {r.status === 'disputed' && r.dispute_reason && (
                            <span className="max-w-[220px] text-xs text-ink-700">“{r.dispute_reason}”</span>
                          )}
                          {r.resolution && (
                            <span className="max-w-[220px] text-xs text-ink-500">
                              {r.resolution}: {r.resolution_notes}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-right">
                        <div className="flex justify-end gap-1">
                          {r.can_confirm && (
                            <Button size="sm" onClick={() => void confirm(r)} disabled={busyId === r.id}>
                              <Check className="mr-1 h-3.5 w-3.5" />
                              {t('verify.records.confirm', 'Confirm')}
                            </Button>
                          )}
                          {r.can_resolve && (
                            <Button size="sm" variant="outline" onClick={() => setAction({ kind: 'resolve', record: r })}>
                              {t('verify.records.resolve', 'Resolve')}
                            </Button>
                          )}
                          {r.status !== 'voided' && r.status !== 'disputed' && r.site_cbo_id !== null && r.site_cbo_id === activeCbo && (
                            <Button size="sm" variant="ghost" onClick={() => setAction({ kind: 'void', record: r })}>
                              {t('verify.records.void', 'Void')}
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      <ActionDialog
        action={action}
        onClose={() => setAction(null)}
        onDone={(r) => {
          replace(r);
          setAction(null);
        }}
      />
    </div>
  );
}

function ActionDialog({
  action,
  onClose,
  onDone,
}: {
  action: Action;
  onClose: () => void;
  onDone: (r: VerifyRecord) => void;
}) {
  const { t } = useAppTranslation();
  const [resolution, setResolution] = useState<'upheld' | 'corrected' | 'voided'>('upheld');
  const [notes, setNotes] = useState('');
  const [hours, setHours] = useState('');
  const [date, setDate] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setResolution('upheld');
    setNotes('');
    setHours(action ? String(action.record.hours) : '');
    setDate(action ? action.record.activity_date : '');
    setError(null);
  }, [action]);

  if (!action) return null;
  const rec = action.record;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (action.kind === 'void') {
        onDone(await verifyApi.voidRecord(rec.id, notes.trim()));
      } else {
        onDone(
          await verifyApi.resolveRecord(rec.id, {
            resolution,
            notes: notes.trim(),
            hours: resolution === 'corrected' && hours ? Number(hours) : undefined,
            activity_date: resolution === 'corrected' && date && date !== rec.activity_date ? date : undefined,
          }),
        );
      }
    } catch (e) {
      setError(errMessage(e, t('verify.records.action_failed', 'That did not work.')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {action.kind === 'void'
              ? t('verify.records.void_title', 'Void this line')
              : t('verify.records.resolve_title', 'Answer the dispute')}
          </DialogTitle>
          <DialogDescription>
            {rec.participant_first_name} {rec.participant_last_name} · {fmtDate(rec.activity_date)} · {fmtHours(rec.hours)} ·{' '}
            {rec.service_description}
          </DialogDescription>
        </DialogHeader>

        {action.kind === 'resolve' && rec.dispute_reason && (
          <div className="rounded-md bg-paper-2 px-3 py-2 text-sm text-ink-700">
            {t('verify.records.member_said', 'The member said:')} “{rec.dispute_reason}”
          </div>
        )}

        <div className="space-y-3">
          {action.kind === 'resolve' && (
            <Field label={t('verify.records.resolution', 'Resolution')}>
              <NativeSelect value={resolution} onChange={(e) => setResolution(e.target.value as typeof resolution)}>
                <option value="upheld">{t('verify.records.upheld', 'The line is right — keep it')}</option>
                <option value="corrected">{t('verify.records.corrected', 'Correct the hours or date')}</option>
                <option value="voided">{t('verify.records.voided', 'The line is wrong — void it')}</option>
              </NativeSelect>
            </Field>
          )}
          {action.kind === 'resolve' && resolution === 'corrected' && (
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('verify.hours', 'Hours')}>
                <input
                  className={inputClass}
                  type="number"
                  min={0.25}
                  max={24}
                  step={0.25}
                  value={hours}
                  onChange={(e) => setHours(e.target.value)}
                />
              </Field>
              <Field label={t('verify.date', 'Date')}>
                <input className={inputClass} type="date" max={todayISO()} value={date} onChange={(e) => setDate(e.target.value)} />
              </Field>
            </div>
          )}
          <Field
            label={
              action.kind === 'void'
                ? t('verify.records.void_reason', 'Why is it being voided?')
                : t('verify.records.note', 'Note to the member')
            }
          >
            <textarea className={`${inputClass} min-h-[80px]`} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>

        <ErrorNote message={error} />

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('verify.cancel', 'Cancel')}
          </Button>
          <Button
            variant={action.kind === 'void' || resolution === 'voided' ? 'danger' : 'primary'}
            onClick={() => void submit()}
            disabled={busy || notes.trim().length < 3}
          >
            {busy
              ? t('verify.saving', 'Saving…')
              : action.kind === 'void'
                ? t('verify.records.void', 'Void')
                : t('verify.records.save_resolution', 'Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
